import { call, put, all, takeLatest, select, cancelled } from 'redux-saga/effects';
import { IAddNewCard, ICreatePaymentIntent, IGetPublishableKey, types } from './types';
import {
    addPaymentMethodFail,
    addPaymentMethodSuccess,
    createPaymentIntentFail,
    createPaymentIntentForPurchaseSuccess,
    createPaymentIntentForPurchasetFail,
    createPaymentIntentSuccess,
    deletePaymentMethodFail,
    deletePaymentMethodSuccess,
    getPaymentMethodsFail,
    getPaymentMethodsSuccess,
    getPaymentRailFail,
    getPaymentRailSuccess,
    getPublishableKeyFail,
    getPublishableKeySuccess,
    updatePaymentMethodFail,
    updatePaymentMethodSuccess,
    verifyFlutterwavePurchaseFail,
    verifyFlutterwavePurchaseSuccess,
    verifyPaystackPurchaseFail,
    verifyPaystackPurchaseSuccess,
} from './action';
import { globalErrorHandler } from '../error/saga';
import { Action } from 'redux';
import { api } from '../../configurations/api';
import {
    addNewPaymentMethodUrl,
    addPaymentMethodUrl,
    createPaymentIntentForSinglePurchaseUrl,
    createPaymentIntentUrl,
    getMyPaymentMethodsUrl,
    getPublishableKeyUrl,
    initializeFlutterwaveBillingCardUrl,
    paymentMethodUrl,
    paymentRailUrl,
    adminPaymentRailCountriesUrl,
    adminAssignPaymentRailCountriesUrl,
    verifyFlutterwaveBillingCardUrl,
    verifyFlutterwavePurchaseUrl,
    verifyPaystackPurchaseUrl,
} from '../../configurations/api/url';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { getCacheByType, getCacheDuration, isUseCacheEnabled } from '../cache/saga';
import { CACHE_TYPE, CacheValue } from '../cache/types';
import { showToastMessage } from '../../utils/AppUtils';
import { IGenericDocumentModifier } from '../../types/global/helper';
import { RootState } from '../root-reducer';
import { updateUserConfig } from '../configuration/action';
import {
    assignAdminPaymentRailCountries,
    assignAdminPaymentRailCountriesFail,
    assignAdminPaymentRailCountriesSuccess,
    fetchAdminPaymentRailCountries,
    fetchAdminPaymentRailCountriesFail,
    fetchAdminPaymentRailCountriesSuccess,
    saveAdminPaymentRailCountries,
    saveAdminPaymentRailCountriesFail,
    saveAdminPaymentRailCountriesSuccess,
} from './action';

function* createPaymentIntent({
    payload,
    resolve,
    reject,
}: {
    payload: ICreatePaymentIntent;
    resolve: any;
    reject: any;
}): Generator<any, any, any> {
    try {
        const response = yield call(api, createPaymentIntentUrl, httpRequest.POST, payload, 3, 100);
        const { clientSecret } = response;
        yield put(createPaymentIntentSuccess(clientSecret));
        if (resolve) resolve(clientSecret);
    } catch (error: any) {
        if (reject) reject(error);
        yield put(createPaymentIntentFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* createPaymentIntentForPurchase({
    payload,
    resolve,
    reject,
}: {
    payload: ICreatePaymentIntent;
    resolve: any;
    reject: any;
}): Generator<any, any, any> {
    try {
        const response = yield call(api, createPaymentIntentForSinglePurchaseUrl, httpRequest.POST, payload, 3, 100);
        const data = response?.data ?? response;
        const responseProvider = String(data.provider || payload.provider || '').toLowerCase();
        if (
            (data.authorization_url || data.access_code) &&
            (responseProvider === 'paystack' || responseProvider === 'flutterwave')
        ) {
            if (!data.provider) data.provider = responseProvider;
            if (resolve) resolve(data);
            return;
        }
        const clientSecret = data.clientSecret ?? data.client_secret;
        yield put(createPaymentIntentForPurchaseSuccess({ clientSecret }));
        if (resolve) resolve(clientSecret);
    } catch (error: any) {
        if (reject) reject(error);
        yield put(createPaymentIntentForPurchasetFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getPaymentRail({
    payload,
    resolve,
    reject,
}: {
    payload: { category?: string };
    resolve?: (data: unknown) => void;
    reject?: (error: unknown) => void;
}): Generator<any, any, any> {
    try {
        const category = payload?.category ?? 'tokens';
        const response = yield call(
            api,
            `${paymentRailUrl}?category=${encodeURIComponent(category)}`,
            httpRequest.GET,
            null,
        );
        const envelope = response?.data ?? response;
        const data = envelope?.data ?? envelope;
        yield put(getPaymentRailSuccess(data));
        if (resolve) resolve(data);
    } catch (error: any) {
        if (reject) reject(error);
        yield put(getPaymentRailFail(error));
    }
}

function settle(
    resolve: ((v: any) => void) | undefined,
    reject: ((e: any) => void) | undefined,
    value: any,
    isError = false,
) {
    if (isError) {
        if (typeof reject === 'function') reject(value);
        return;
    }
    if (typeof resolve === 'function') resolve(value);
}

function* fetchAdminPaymentRailCountriesSaga({
    resolve,
    reject,
}: {
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const response = yield call(api, adminPaymentRailCountriesUrl, httpRequest.GET, null, 2, 0);
        const data = response?.data ?? response ?? {};
        yield put(fetchAdminPaymentRailCountriesSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchAdminPaymentRailCountriesFail(error));
        settle(resolve, reject, error, true);
    }
}

function* saveAdminPaymentRailCountriesSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { assignments: Record<string, string[]> };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const response = yield call(
            api,
            adminPaymentRailCountriesUrl,
            httpRequest.PUT,
            { assignments: payload.assignments },
            2,
            0,
        );
        const data = response?.data ?? response ?? {};
        yield put(saveAdminPaymentRailCountriesSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(saveAdminPaymentRailCountriesFail(error));
        settle(resolve, reject, error, true);
    }
}

function* assignAdminPaymentRailCountriesSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { rail: string; countries: string[] };
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const response = yield call(
            api,
            adminAssignPaymentRailCountriesUrl,
            httpRequest.POST,
            { rail: payload.rail, countries: payload.countries },
            2,
            0,
        );
        const data = response?.data ?? response ?? {};
        yield put(assignAdminPaymentRailCountriesSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(assignAdminPaymentRailCountriesFail(error));
        settle(resolve, reject, error, true);
    }
}

function* verifyPaystackPurchaseSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { reference: string };
    resolve?: (data: unknown) => void;
    reject?: (error: unknown) => void;
}): Generator<any, any, any> {
    let settled = false;
    try {
        const response = yield call(api, verifyPaystackPurchaseUrl, httpRequest.POST, {
            reference: payload.reference,
        });
        const envelope = response?.data ?? response;
        const data = envelope?.data ?? envelope;
        yield put(verifyPaystackPurchaseSuccess(data));
        if (resolve) {
            settled = true;
            resolve(data);
        }
    } catch (error: any) {
        if (reject) {
            settled = true;
            reject(error);
        }
        yield put(verifyPaystackPurchaseFail(error));
        throw error;
    } finally {
        // A superseded verification must still settle, otherwise the callback screen spins forever.
        if ((yield cancelled()) && !settled && reject) {
            reject(new Error('Payment verification cancelled'));
        }
    }
}

function* getPublishableKey({ payload }: { payload: IGetPublishableKey }): any {
    try {
        const response = yield call(
            api,
            `${getPublishableKeyUrl}?source=${payload.source}`,
            httpRequest.GET,
            null,
            2,
            1000,
        );
        // `api` already returns axios `response.data` (the JSON body).
        const publishableKey = response?.publishableKey ?? response?.publishable_key;
        const isNonUs = Boolean(response?.isNonUs ?? response?.is_non_us);
        const provider = (response?.provider as IGetPublishableKey['source'] | undefined) ?? payload.source;
        yield put(getPublishableKeySuccess({ isNonUs, publishableKey, provider }));
    } catch (error: any) {
        yield put(getPublishableKeyFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* getPaymentMethods(): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    const defaultCacheDuration = yield* getCacheDuration();
    let initialResult: any = null;
    const cache: CacheValue = yield* getCacheByType(CACHE_TYPE.PAYMENT);

    if (
        !!initialResult &&
        defaultUseCache &&
        ((Date.now() - Number(cache.key)) as unknown as number) < defaultCacheDuration
    ) {
        yield put(getPaymentMethodsSuccess(initialResult));
    } else {
        try {
            const response = yield call(api, getMyPaymentMethodsUrl, httpRequest.GET, {}, 0, 0);
            const { data } = response.data;
            yield put(getPaymentMethodsSuccess(data));
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getPaymentMethodsFail(error));
            yield call(globalErrorHandler, error);
        }
    }
}

function* addPaymenMethods({ payload, resolve, reject }: { payload: IAddNewCard; resolve: any; reject: any }): any {
    // Paystack popup returns { reference, isNonUs: true }; Stripe uses payment_method / intent.
    const isPaystack = Boolean(payload?.isNonUs || payload?.reference || payload?.trxref);
    const request = isPaystack ? addNewPaymentMethodUrl : addPaymentMethodUrl;
    const body = isPaystack
        ? { reference: payload.reference || payload.trxref || payload.trans || payload.transaction }
        : payload;

    try {
        if (isPaystack && !body.reference) {
            throw new Error('Paystack reference is required to save this card');
        }
        const response = yield call(api, request, httpRequest.POST, body, 0, 0);
        // api may return axios-shaped { data: apiBody } or already-unwrapped body
        const apiBody = response?.data ?? response;
        const nested = apiBody?.data ?? apiBody;
        const { charge, authorization, refund } = nested || {};

        if (authorization && charge && refund) {
            const paymentMethod = authorization.data ?? authorization;
            yield put(addPaymentMethodSuccess(paymentMethod));
            if (refund) {
                yield call(showToastMessage, charge.message);
                yield call(showToastMessage, refund.message);
            }
            const state: RootState = yield select();
            const { paymentMethods } = state.payment;
            if (paymentMethods?.length === 1 && paymentMethod?._id) {
                yield put(
                    updateUserConfig({
                        payload: {
                            default_payment_method: paymentMethod._id,
                        },
                    }),
                );
            }
            yield call(showToastMessage, 'New Card Added Successfully', 'success');
            if (resolve) resolve(paymentMethod);
            return;
        }

        const paymentMethod = nested?._id ? nested : apiBody;
        const state: RootState = yield select();
        const { paymentMethods } = state.payment;
        yield put(addPaymentMethodSuccess(paymentMethod));
        if (paymentMethods?.length === 1 && paymentMethod?._id) {
            yield put(
                updateUserConfig({
                    payload: {
                        default_payment_method: paymentMethod._id,
                    },
                }),
            );
        }
        yield call(showToastMessage, 'New Card Added Successfully', 'success');
        if (resolve) resolve(paymentMethod);
    } catch (error: any) {
        if (reject) reject(error);
        const duplicateSignature =
            error?.code === 'DUPLICATE_FIELD' ||
            error?.data?.code === 'DUPLICATE_FIELD' ||
            error?.details?.code === 'DUPLICATE_FIELD' ||
            (Array.isArray(error?.data?.fields) && error.data.fields.includes('signature')) ||
            (Array.isArray(error?.details?.fields) && error.details.fields.includes('signature'));
        if (duplicateSignature) {
            // Card may already be stored from a prior attempt — refresh list instead of only failing.
            yield put({ type: types.GET_PAYMENT_METHODS });
            yield call(showToastMessage, 'This card may already be on file. Refreshing payment methods…', 'info');
        } else {
            yield call(showToastMessage, error);
        }
        yield put(addPaymentMethodFail(error));
    }
}

function* updatePaymenMethod({ payload }: { payload: any }): any {
    try {
        const response = yield call(api, addPaymentMethodUrl, httpRequest.GET, payload, 0, 0);
        const { data } = response.data;
        yield put(updatePaymentMethodSuccess(data));
    } catch (error: any) {
        yield call(showToastMessage, error);
        yield put(updatePaymentMethodFail(error));
    }
}

function* deletePaymenMethod({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        yield call(api, `${paymentMethodUrl}${payload.documentId}`, httpRequest.DELETE, payload, 0, 0);
        yield put(deletePaymentMethodSuccess(payload));
    } catch (error: any) {
        yield call(showToastMessage, error);
        yield put(deletePaymentMethodFail(error));
    }
}

interface TaskAction extends Action {
    type: string;
    payload: any;
    reject: any;
    resolve: any;
}

function* getPaymentMethodsWatcher() {
    yield takeLatest<TaskAction>(types.GET_PAYMENT_METHODS, getPaymentMethods);
}

function* addPaymenMethodsWatcher() {
    yield takeLatest<TaskAction>(types.ADD_PAYMENT_METHOD, addPaymenMethods);
}

function* updatePaymenMethodsWatcher() {
    yield takeLatest<TaskAction>(types.UPDATE_PAYMENT_METHOD, updatePaymenMethod);
}

function* deletePaymenMethodWatcher() {
    yield takeLatest<TaskAction>(types.DELETE_PAYMENT_METHOD, deletePaymenMethod);
}

function* createPaymentIntentWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_PAYMENT_INTENT, createPaymentIntent);
}

function* createPaymentIntentForPurchaseWatcher() {
    yield takeLatest<TaskAction>(types.CREATE_PAYMENT_INTENT_FOR_PURCHASE, createPaymentIntentForPurchase);
}

function* getPublishableKeyWatcher() {
    yield takeLatest<TaskAction>(types.GET_PUBLISHABLE_KEY, getPublishableKey);
}

function* getPaymentRailWatcher() {
    yield takeLatest<TaskAction>(types.GET_PAYMENT_RAIL, getPaymentRail);
}

function* verifyPaystackPurchaseWatcher() {
    yield takeLatest<TaskAction>(types.VERIFY_PAYSTACK_PURCHASE, verifyPaystackPurchaseSaga);
}

function* verifyFlutterwavePurchaseSaga({
    payload,
    resolve,
    reject,
}: {
    payload: { transaction_id?: string; tx_ref?: string; reference?: string };
    resolve?: (data: unknown) => void;
    reject?: (error: unknown) => void;
}): Generator<any, any, any> {
    let settled = false;
    try {
        const body: Record<string, string> = {};
        if (payload.transaction_id) body.transaction_id = payload.transaction_id;
        if (payload.tx_ref) body.tx_ref = payload.tx_ref;
        if (payload.reference) body.reference = payload.reference;
        const response = yield call(api, verifyFlutterwavePurchaseUrl, httpRequest.POST, body);
        const envelope = response?.data ?? response;
        const data = envelope?.data ?? envelope;
        yield put(verifyFlutterwavePurchaseSuccess(data));
        if (resolve) {
            settled = true;
            resolve(data);
        }
    } catch (error: any) {
        if (reject) {
            settled = true;
            reject(error);
        }
        yield put(verifyFlutterwavePurchaseFail(error));
        throw error;
    } finally {
        if ((yield cancelled()) && !settled && reject) {
            reject(new Error('Payment verification cancelled'));
        }
    }
}

function* verifyFlutterwavePurchaseWatcher() {
    yield takeLatest<TaskAction>(types.VERIFY_FLUTTERWAVE_PURCHASE, verifyFlutterwavePurchaseSaga);
}

function* initializeFlutterwaveBillingCardWorker({ payload, resolve, reject }: any): any {
    try {
        const response = yield call(api, initializeFlutterwaveBillingCardUrl, httpRequest.POST, payload, 2, 3000);
        const { data } = response;
        if (resolve) resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* verifyFlutterwaveBillingCardWorker({ payload, resolve, reject }: any): any {
    try {
        const body: Record<string, string> = {};
        if (payload.transaction_id) body.transaction_id = payload.transaction_id;
        if (payload.tx_ref) body.tx_ref = payload.tx_ref;
        if (payload.reference) body.reference = payload.reference;
        const response = yield call(api, verifyFlutterwaveBillingCardUrl, httpRequest.POST, body, 2, 3000);
        const envelope = response?.data ?? response;
        const data = envelope?.data ?? envelope;
        const paymentMethod = data?.payment_method ?? data;
        if (paymentMethod?._id) {
            yield put(addPaymentMethodSuccess(paymentMethod));
            const state: RootState = yield select();
            const methods = state.payment.paymentMethods;
            if (methods?.length === 1) {
                yield put(
                    updateUserConfig({
                        payload: {
                            default_payment_method: paymentMethod._id,
                        },
                    }),
                );
            }
        }
        yield call(
            showToastMessage,
            data?.already_saved ? 'Card already on file' : 'New Card Added Successfully',
            'success',
        );
        if (resolve) resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* initializeFlutterwaveBillingCardWatcher() {
    yield takeLatest<TaskAction>(types.INITIALIZE_FLUTTERWAVE_BILLING_CARD, initializeFlutterwaveBillingCardWorker);
}

function* verifyFlutterwaveBillingCardWatcher() {
    yield takeLatest<TaskAction>(types.VERIFY_FLUTTERWAVE_BILLING_CARD, verifyFlutterwaveBillingCardWorker);
}

export default function* paymentSaga() {
    yield all([
        getPaymentMethodsWatcher(),
        addPaymenMethodsWatcher(),
        updatePaymenMethodsWatcher(),
        deletePaymenMethodWatcher(),
        getPublishableKeyWatcher(),
        createPaymentIntentWatcher(),
        createPaymentIntentForPurchaseWatcher(),
        getPaymentRailWatcher(),
        takeLatest(fetchAdminPaymentRailCountries as any, fetchAdminPaymentRailCountriesSaga),
        takeLatest(saveAdminPaymentRailCountries as any, saveAdminPaymentRailCountriesSaga),
        takeLatest(assignAdminPaymentRailCountries as any, assignAdminPaymentRailCountriesSaga),
        verifyPaystackPurchaseWatcher(),
        verifyFlutterwavePurchaseWatcher(),
        initializeFlutterwaveBillingCardWatcher(),
        verifyFlutterwaveBillingCardWatcher(),
    ]);
}
